// Location/city data for /locations/:city landing pages.
//
// DATA-DRIVEN BY DESIGN: to add a new service-area city later, add one entry
// here — the LocationPage component, sitemap note, and Footer links all read
// from this list. Keep copy UNIQUE per city (real local character, areas, and
// angle) — thin/duplicated city pages read as doorway pages and get penalized.
//
// Each entry:
//   slug        URL segment (/locations/<slug>)
//   name        Display city name
//   county      County (used in copy + schema)
//   tagline     Hero subhead
//   intro       Array of paragraphs — unique, locally-grounded
//   angle       Why listing media matters specifically in this market
//   areas       Neighborhoods / sub-areas served (shown as a list + in copy)
//   nearby      slugs of nearby cities for internal cross-linking

const LOCATIONS = [
  {
    slug: 'eagle-mountain',
    name: 'Eagle Mountain',
    county: 'Utah County',
    tagline: 'Real estate photography across one of Utah’s fastest-growing cities',
    intro: [
      `Eagle Mountain has grown from a quiet western-Utah County community into one of the state’s fastest-expanding cities, with new master-planned neighborhoods going up against a backdrop of open foothills and wide valley views. That growth means listings here compete hard — buyers scrolling Zillow and the MLS are comparing dozens of similar new builds, and the photos are what make one stop the scroll.`,
      `We shoot homes throughout Eagle Mountain, from new construction in The Ranches and City Center to larger view lots out toward Cedar Pass and Pony Express. Big skies and sweeping lots are an asset here, which is exactly why aerial and twilight work tends to pay off so well on local listings.`
    ],
    angle: 'Sprawling new builds and large view lots make drone, aerial context shots, and twilight especially effective — they show the land, the views, and the scale that ground-level photos can’t.',
    areas: ['City Center', 'The Ranches', 'Silverlake', 'Overland', 'SpringRun', 'Cedar Pass', 'Pony Express Parkway corridor'],
    nearby: ['saratoga-springs', 'lehi']
  },
  {
    slug: 'saratoga-springs',
    name: 'Saratoga Springs',
    county: 'Utah County',
    tagline: 'Lakeside real estate photography on the shores of Utah Lake',
    intro: [
      `Saratoga Springs has become one of Utah County’s most sought-after addresses, blending master-planned family neighborhoods with a genuine lakeside setting along Utah Lake. With the marina, trail network, and mountain views all part of the draw, the best listings here sell a lifestyle as much as a floor plan.`,
      `We photograph homes from The Crossroads and Harvest Hills to newer phases near the lake, capturing both the interiors and the surroundings that make Saratoga Springs distinctive — water, open space, and the Wasatch beyond.`
    ],
    angle: 'Lake proximity, marina access, and mountain views reward aerial photography and twilight shots that put a home in its setting — a major differentiator on family-focused listings.',
    areas: ['The Crossroads', 'Harvest Hills', 'Talons Cove', 'Israel Canyon area', 'Pelican Bay / lakeside neighborhoods'],
    nearby: ['eagle-mountain', 'lehi']
  },
  {
    slug: 'lehi',
    name: 'Lehi',
    county: 'Utah County',
    tagline: 'Real estate photography in the heart of Silicon Slopes',
    intro: [
      `Lehi sits at the center of Silicon Slopes, Utah’s tech corridor, which shapes its housing market in a specific way: a steady stream of relocating professionals and out-of-state buyers, many of whom shortlist homes online before they ever fly in. For those buyers, the photos, video, and 3D tour aren’t a nice-to-have — they’re the showing.`,
      `From modern homes on Traverse Mountain to established family neighborhoods and the developments around Thanksgiving Point, we produce listing media built to convert remote and relocating buyers as effectively as local ones.`
    ],
    angle: 'A high share of relocating and out-of-state buyers makes cinematic video and Matterport 3D tours especially valuable — they let someone tour a Lehi home thoroughly without setting foot in Utah.',
    areas: ['Traverse Mountain', 'Holbrook Farms', 'Thanksgiving Point area', 'Spring Creek', 'Lehi Tech / Silicon Slopes corridor'],
    nearby: ['eagle-mountain', 'saratoga-springs']
  },
  {
    slug: 'provo-orem',
    name: 'Provo & Orem',
    county: 'Utah County',
    tagline: 'Real estate photography for Utah County’s university communities',
    intro: [
      `Provo and Orem anchor central Utah County, home to BYU and UVU and a housing market that spans first homes, established family neighborhoods, and a deep pool of student-rental and investment property. That mix means a listing photographer here needs range — the shot list for a duplex investment looks nothing like a forever home backing onto Provo Canyon.`,
      `We cover both cities, from Edgemont and Grandview to downtown Provo and the Orem and Lindon benches, with photography that suits whatever the property is being sold as: a home, an income property, or both.`
    ],
    angle: 'A large investment- and rental-property segment alongside family homes calls for clean, accurate documentation — and canyon and bench backdrops give exterior and drone shots a real lift.',
    areas: ['Downtown Provo', 'Edgemont', 'Grandview', 'Orem benches', 'Lindon', 'Provo Canyon corridor'],
    nearby: ['lehi', 'salt-lake-city']
  },
  {
    slug: 'salt-lake-city',
    name: 'Salt Lake City',
    county: 'Salt Lake County',
    tagline: 'Real estate photography across the capital city and the benches',
    intro: [
      `Salt Lake City’s housing stock is the most varied in the state — historic homes in The Avenues, walkable bungalows in Sugar House, downtown condos, and luxury bench properties with valley-wide views. Each calls for a different eye, and a one-size-fits-all approach tends to undersell exactly what makes a given listing special.`,
      `We shoot throughout the city and the east bench, tailoring the lighting and composition to the property — preserving the character of a historic home, emphasizing light and flow in a condo, or showcasing the views and architecture of a luxury listing.`
    ],
    angle: 'A wide range of property types — historic, urban condo, and luxury bench homes — rewards a photographer who adapts; HDR interiors preserve character while twilight and drone elevate view properties.',
    areas: ['The Avenues', 'Sugar House', 'Federal Heights', 'Downtown', 'Millcreek', 'East bench neighborhoods'],
    nearby: ['provo-orem', 'park-city']
  },
  {
    slug: 'park-city',
    name: 'Park City',
    county: 'Summit County',
    tagline: 'Luxury and resort real estate photography in the mountains',
    intro: [
      `Park City is Utah’s premier luxury and resort market, where ski-in/ski-out properties, mountain estates, and second homes change hands with buyers who are often shopping from out of state — or out of the country. At this end of the market, presentation is the price of entry: the media has to match the property.`,
      `We produce premium listing packages for Park City and the surrounding Snyderville Basin, from Old Town to Deer Valley and the gated communities along the benches, with the cinematic video, twilight, aerial, and 3D coverage that high-end listings are expected to have.`
    ],
    angle: 'Luxury and second-home buyers expect cinematic video, twilight, aerial, and Matterport 3D as standard — premium media isn’t optional in this market, it’s what gets a listing taken seriously.',
    areas: ['Old Town', 'Deer Valley', 'Canyons Village', 'Promontory', 'Glenwild', 'Snyderville Basin'],
    nearby: ['salt-lake-city', 'ogden']
  },
  {
    slug: 'ogden',
    name: 'Ogden',
    county: 'Weber County',
    tagline: 'Real estate photography across northern Utah’s mountain city',
    intro: [
      `Ogden pairs historic character — the brick storefronts of 25th Street, early-1900s homes on the east bench — with quick access to the mountains and a downtown that keeps drawing new interest. It’s a market with genuine personality, and the listings that do best are the ones whose photos capture it.`,
      `We photograph homes across Ogden and up into Ogden Valley, from east-bench properties with Wasatch views to cabins and homes near Eden and Huntsville, matching the media to whether a property is selling on charm, views, or mountain access.`
    ],
    angle: 'Historic homes reward careful interior work that preserves character, while bench and Ogden Valley properties shine with drone and exterior context shots that show off the mountain setting.',
    areas: ['East Bench', 'Downtown / 25th Street', 'Ogden Valley', 'Eden', 'Huntsville', 'North Ogden'],
    nearby: ['salt-lake-city', 'park-city']
  }
];

export const getLocation = (slug) => LOCATIONS.find((l) => l.slug === slug) || null;

export default LOCATIONS;
